import React from 'react';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(LinearScale, PointElement, ChartTooltip, Legend, ChartDataLabels);

export default function PlayerMatrix({ collaborators, onPlayerClick, areaFilter, liderFilter }) {
  // Define quadrant colors
  const quadrantColors = {
    A: 'rgba(76, 175, 80, 0.6)', // Green
    B: 'rgba(255, 193, 7, 0.6)',  // Yellow
    C: 'rgba(33, 150, 243, 0.6)', // Blue
    D: 'rgba(244, 67, 54, 0.6)',  // Red
  };

  const filteredCollaborators = collaborators.filter(c => {
    const matchesArea = areaFilter.length === 0 || areaFilter.includes(c.area);
    const matchesLider = liderFilter.length === 0 || liderFilter.includes(c.lider);
    return matchesArea && matchesLider;
  });

  // Prepare data for Chart.js
  const data = {
    datasets: [
      {
        label: 'Colaboradores',
        data: filteredCollaborators
          .filter(c => c && typeof c.puntaje_desempeno !== 'undefined' && typeof c.puntaje_fit_cultural !== 'undefined' && c.iniciales)
          .map(c => ({
          x: c.puntaje_desempeno, // Performance Score
          y: c.puntaje_fit_cultural, // Cultural Fit Score
          initials: c.iniciales,
          area: c.area,
          lider: c.lider,
          label:"tre",
          nombre: c.nombre,
          apellido: c.apellido,
        })),
        backgroundColor: filteredCollaborators.map(c => {
          if (!c) return 'rgba(128, 128, 128, 0.6)'; // Handle null/undefined collaborators
          const performance = c.puntaje_desempeno;
          const culturalFit = c.puntaje_fit_cultural;

          if (performance >= 1 && performance <= 3 && culturalFit >= 1 && culturalFit <= 2) {
            return quadrantColors.A; // Altamente alineados
          } else if (performance >= 1 && performance <= 3 && culturalFit >= 3 && culturalFit <= 4) {
            return quadrantColors.B; // Buen rendimiento, bajo fit
          } else if (performance >= 4 && performance <= 5 && culturalFit >= 1 && culturalFit <= 2) {
            return quadrantColors.C; // Buen fit, bajo rendimiento
          } else if (performance >= 4 && performance <= 5 && culturalFit >= 3 && culturalFit <= 4) {
            return quadrantColors.D; // Doble riesgo
          }
          return 'rgba(128, 128, 128, 0.6)'; // Default for out-of-quadrant
        }),
        pointRadius: 8,
        pointHoverRadius: 10,
      },
    ],
  };

  // Chart.js options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Puntaje de Desempeño (1 = Top Performer, 5 = Bajo Desempeño)',
        },
        min: 1,
        max: 5,
        ticks: { stepSize: 1 },
      },
      y: {
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Puntaje de Valores / Fit Cultural (1 = Alto Fit, 4 = Bajo Fit)',
        },
        min: 1,
        max: 4,
        reverse: true, // Invert Y axis so 1 is at the top, 4 at the bottom (matches description)
        ticks: { stepSize: 1 },
      },
    },
    plugins: {
      legend: {
        display: false, // Hide default legend
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const dataPoint = context.raw;
            console.log('dataPoint in PlayerMatrix tooltip label:', dataPoint); // Debugging
            if (dataPoint && dataPoint.nombre && dataPoint.apellido) {
              return `${dataPoint.nombre} ${dataPoint.apellido}`;
            }
            return '';
          },
          title: function (context) {
            return ''; // Hide default title
          },
          itemSort: (a, b) => 0, // Prevent default item sorting/display logic
        },
        displayColors: false, // Hide the color box next to the label
        mode: 'point', // Ensure tooltip only shows for the hovered point
        intersect: false, // Ensure tooltip does not require intersecting with the data point
      },
      datalabels: { // Explicitly disable datalabels if the plugin is active
        display: true,
        formatter: function (value, context) {
          console.log('Datalabels formatter value:', value, 'context:', context);
          if (value.initials) {
            return value.initials;
          }
          return ''; // Return empty string if initials are not available
        },
        color: 'black', // Color of the initials on the point
        font: {
          weight: 'bold',
          size: 10,
        },
        align: 'center',
        anchor: 'center',
      },
    },
    // Custom drawing for quadrants and initials
    // This will require a Chart.js plugin or custom element, which is more complex.
    // For now, we will rely on background colors and tooltip for interaction.
    // The initials on points are not directly supported by Chart.js scatter out of the box.
    // Will explore a custom plugin or use SVG for more precise control if needed.

    onClick: (event, elements) => {
      console.log('PlayerMatrix click event fired!', elements);
      if (elements.length > 0) {
        const clickedElementIndex = elements[0].index;
        const clickedCollaborator = collaborators[clickedElementIndex];
        onPlayerClick(clickedCollaborator);
      }
    },
  };

  if (!collaborators || collaborators.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', textAlign: 'center' }}>
        <Typography variant="subtitle1">No hay datos de colaboradores disponibles para el período y filtros seleccionados.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', height: '500px' }}>
      <Typography variant="h5" mb={2}>Matriz de Jugadores (Fit Cultural vs Desempeño)</Typography>
      <Box sx={{ height: '400px' }}>
        <Scatter key={JSON.stringify(collaborators)} data={data} options={options} />
      </Box>
    </Paper>
  );
} 